import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, switchMap } from 'rxjs/operators';
import { combineLatestWith, filter, of } from 'rxjs';
import { MetadataActions } from '@rillion/store/metadata/metadata.actions';
import { AuthActions } from '@rillion/store/auth/auth.actions';
import { GroupTypeService } from '../../../libs/system';
import { MasterDataApiService } from '@rillion/api/master-data-api.service';
import { AuthSelectors } from '../auth';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastService } from '@rillion/core/services/toast.service';
import { AuthApiService } from '@rillion/auth';

@Injectable()
export class MetadataEffects {
  constructor(
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly authApiService: AuthApiService,
    private readonly groupTypeService: GroupTypeService,
    private readonly masterDataApiService: MasterDataApiService,
    private readonly toastService: ToastService,
  ) {}

  getRolesOnLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.getUserSuccess),
      map(() => MetadataActions.getRoles()),
    ),
  );

  getGroupTypesForAccountCodingGridView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.getUserSuccess),
      map(() => MetadataActions.getGroupTypes({ template: 'invoiceAccountCodingGridView' })),
    ),
  );

  getAccountsOnLoginAndRoleChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.getUserSuccess),
      map(() => MetadataActions.getAccounts()),
    ),
  );

  getVatCodesOnLoginAndRoleChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.getUserSuccess),
      map(() => MetadataActions.getVatCodes()),
    ),
  );

  getObjectRelationsOnLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.getUserSuccess),
      map(() => MetadataActions.getObjectRelations()),
    ),
  );

  getObjectRelationSettingsOnLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.getUserSuccess),
      map(() => MetadataActions.getObjectRelationSettings()),
    ),
  );

  getGroupTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MetadataActions.getGroupTypes),
      exhaustMap(({ template }) => {
        return this.groupTypeService.apiV10GroupTypeGet(template).pipe(
          map((groupTypes) => MetadataActions.getGroupTypesSuccess({ template, groupTypes })),
          catchError((error: HttpErrorResponse) => {
            this.toastService.error('Problem getting group types: ' + error?.error?.error?.message);
            return of(MetadataActions.getGroupTypesFailure({ error }));
          }),
        );
      }),
    ),
  );

  getAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MetadataActions.getAccounts),
      map(() => MetadataActions.getAccountsSuccess({ accounts: [] })),
    ),
  );

  getVatCodes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MetadataActions.getVatCodes),
      combineLatestWith(this.store.select(AuthSelectors.selectUserRole)),
      filter(([, role]) => !!role),
      switchMap(([, role]) => {
        return this.masterDataApiService.getVatCodes(role).pipe(
          map(({ data: vatCodes }) => MetadataActions.getVatCodesSuccess({ vatCodes })),
          catchError((error: HttpErrorResponse) => {
            this.toastService.error('Problem getting vat codes: ' + error?.error?.error?.message);
            return of(MetadataActions.getVatCodesFailure({ error }));
          }),
        );
      }),
    ),
  );

  getObjectRelations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MetadataActions.getObjectRelations),
      switchMap(() => {
        return this.masterDataApiService.getObjectRelations().pipe(
          map(({ data: objectRelations }) => MetadataActions.getObjectRelationsSuccess({ objectRelations })),
          catchError((error: HttpErrorResponse) => {
            this.toastService.error('Problem getting object relations: ' + error?.error?.error?.message);
            return of(MetadataActions.getObjectRelationsFailure({ error }));
          }),
        );
      }),
    ),
  );

  getObjectRelationSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MetadataActions.getObjectRelationSettings),
      switchMap(() => {
        return this.masterDataApiService.getObjectRelationSettings().pipe(
          map(({ data: objectRelationSettings }) => MetadataActions.getObjectRelationSettingsSuccess({ objectRelationSettings })),
          catchError((error: HttpErrorResponse) => {
            this.toastService.error('Problem getting object relation settings: ' + error?.error?.error?.message);
            return of(MetadataActions.getObjectRelationSettingsFailure({ error }));
          }),
        );
      }),
    ),
  );
}
